import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ToggleLeft, ShieldCheck } from "@phosphor-icons/react";

const CATEGORIES = [
  { key: "all",        label: "All" },
  { key: "reports",    label: "Reports" },
  { key: "governance", label: "Governance" },
  { key: "comms",      label: "Comms" },
  { key: "osint",      label: "OSINT" },
  { key: "finance",    label: "Finance" },
  { key: "admin",      label: "Admin" },
  { key: "ux",         label: "UX" },
];

function StatePill({ state }) {
  const tone = state === "on" ? "bg-emerald-100 text-emerald-700 border-emerald-200"
             : state === "off" ? "bg-rose-100 text-rose-700 border-rose-200"
             : "bg-[var(--surface-3)] text-[var(--text-secondary)] border-[var(--border)]";
  return (
    <span data-testid={`flag-state-pill-${state}`} className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full border ${tone}`}>
      {state}
    </span>
  );
}

export default function FeatureToggles() {
  const { user } = useAuth();
  const tier = user?.ux_tier ?? 0;
  const canMutate = tier >= 5;
  const [flags, setFlags] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/feature-flags");
      setFlags(data.flags || []);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Failed to load flags."); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (tier >= 4) load(); }, [tier]);

  const setGlobal = async (flag_key, new_state) => {
    try {
      await api.put(`/feature-flags/${flag_key}`, { global_state: new_state });
      await load();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Update failed."); }
  };

  if (tier < 4) {
    return (
      <div data-testid="feature-toggles-page" className="p-6 text-[var(--text-secondary)]">
        <ShieldCheck size={32} className="text-[var(--text-faint)] mb-3" />
        Feature toggles admin requires Tier 4+ (COO/CEO).
      </div>
    );
  }

  const visible = filter === "all" ? flags : flags.filter(f => f.category === filter);

  return (
    <div data-testid="feature-toggles-page" className="p-6 space-y-5 max-w-6xl">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#263776] flex items-center gap-2">
            <ToggleLeft size={28} className="text-[#F47D20]" />
            Feature Toggles
          </h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">CEO-controlled kill-switches. Resolution order: User → Department → Global → Default.</p>
        </div>
        <div className="text-xs text-[var(--text-muted)]">
          {flags.length} flags · {flags.filter(f => f.effective_for_caller).length} on for you
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {CATEGORIES.map(c => (
          <button
            key={c.key}
            onClick={() => setFilter(c.key)}
            data-testid={`flag-filter-${c.key}`}
            className={`text-xs px-3 py-1 rounded-full border transition ${
              filter === c.key
                ? "bg-[#263776] text-white border-[#263776]"
                : "bg-white text-[var(--text-secondary)] border-[var(--border)] hover:border-[#263776]"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {err && <div data-testid="flag-error" className="bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2 rounded">{err}</div>}
      {loading ? <div className="text-[var(--text-muted)] text-sm">Loading…</div> : (
        <div className="bg-white border border-[var(--border)] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--surface-2)] text-[var(--text-secondary)]">
              <tr>
                <th className="text-left px-3 py-2">Flag</th>
                <th className="text-left px-3 py-2">Category</th>
                <th className="text-left px-3 py-2">Default</th>
                <th className="text-left px-3 py-2">Global</th>
                <th className="text-left px-3 py-2">For you</th>
                {canMutate && <th className="text-right px-3 py-2">Set global</th>}
              </tr>
            </thead>
            <tbody>
              {visible.map(f => (
                <tr key={f.flag_key} data-testid={`flag-row-${f.flag_key}`} className="border-t border-[var(--border)] hover:bg-[var(--surface-2)]/60">
                  <td className="px-3 py-2">
                    <div className="font-medium text-[var(--text-primary)]">{f.label}</div>
                    <div className="text-[11px] text-[var(--text-muted)]">{f.description}</div>
                    <code className="text-[10px] text-[var(--text-faint)]">{f.flag_key}</code>
                  </td>
                  <td className="px-3 py-2 capitalize text-[var(--text-secondary)]">{f.category}</td>
                  <td className="px-3 py-2"><StatePill state={f.default_state} /></td>
                  <td className="px-3 py-2"><StatePill state={f.global_state} /></td>
                  <td className="px-3 py-2">
                    <span data-testid={`flag-effective-${f.flag_key}`} className={f.effective_for_caller ? "text-emerald-700 font-semibold" : "text-[var(--text-faint)]"}>
                      {f.effective_for_caller ? "ON" : "OFF"}
                    </span>
                  </td>
                  {canMutate && (
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex border border-[var(--border)] rounded overflow-hidden">
                        {["on", "default", "off"].map(s => (
                          <button
                            key={s}
                            onClick={() => setGlobal(f.flag_key, s)}
                            data-testid={`flag-set-${f.flag_key}-${s}`}
                            className={`px-2 py-0.5 text-[11px] transition ${
                              f.global_state === s
                                ? (s === "on" ? "bg-emerald-600 text-white" : s === "off" ? "bg-rose-600 text-white" : "bg-slate-600 text-white")
                                : "bg-white text-[var(--text-muted)] hover:bg-[var(--surface-3)]"
                            }`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {visible.length === 0 && (
                <tr><td colSpan={canMutate ? 6 : 5} className="px-3 py-6 text-center text-[var(--text-faint)] text-sm">No flags in this category.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
